<script setup lang="ts">
import { useResizeObserver } from "@vueuse/core";
import { computed, nextTick, onUnmounted, ref, watch } from "vue";
import type { ComponentExposed } from "vue-component-type-helpers";

import actions from "~/actions";
import { getPropertyConfig, getPropertyDisplayTitle, getPropertyValueFromTask } from "~/common/properties";
import AiSpinner from "~/components/dumb/AiSpinner.vue";
import DropdownMenuItemContent from "~/components/dumb/DropdownMenuItemContent.vue";
import FillOutWithAiItem from "~/components/dumb/FillOutWithAiItem.vue";
import MultiselectDropdownMenu from "~/components/dumb/MultiselectDropdownMenu.vue";
import Tooltip from "~/components/dumb/Tooltip.vue";
import Chip from "~/components/filters/ValueFilter/Chip.vue";
import { notify } from "~/components/notifications";
import OptionComponent from "~/components/options/Option.vue";
import OptionDropdownItem from "~/components/options/OptionDropdownItem.vue";
import { UNSET_PROPERTY_PSEUDO_VALUE } from "~/constants/pseudoDuids";
import { colorsByTheme } from "~/constants/style";
import { PlusIcon, TagsFieldIcon, XIcon } from "~/icons";
import { CommandId, EditorMode, NotificationType, Placement } from "~/shared/enums";
import type { PropertyAnyMultiselect, PropertyValueForKind, Task } from "~/shared/types";
import { useAppStore, useDataStore, usePageStore } from "~/stores";
import { intersectionOfAllSets, unionOfAllSets } from "~/utils/common";
import { generateFieldRecommendation } from "~/utils/recommendation";
import { makeValidateOptionTitle } from "~/utils/validation";

const props = defineProps<{
  property: PropertyAnyMultiselect;
  tasks: Task[];
  defaultValue?: PropertyValueForKind<PropertyAnyMultiselect>;
  editorMode: EditorMode;
  values?: string[];
  onAfterClose?: (values: string[]) => void;
  onClose?: () => void;
}>();

const emit = defineEmits<{
  update: [values?: string[]];
  reject: [];
}>();

const appStore = useAppStore();
const dataStore = useDataStore();
const pageStore = usePageStore();

const colors = computed(() => colorsByTheme[pageStore.theme]);

const dropdownMenu = ref<ComponentExposed<typeof MultiselectDropdownMenu> | null>(null);
const wrapper = ref<HTMLDivElement | null>(null);

const propertyConfig = computed(() => getPropertyConfig(props.property.kind));
const isDefault = computed(() => propertyConfig.value.isDefault);

const loading = computed(() => appStore.isLoadingTasksProperty(props.tasks, props.property.duid));

const isListMode = computed(() => props.editorMode === EditorMode.LIST);
const isChipRecommendationMode = computed(() => props.editorMode === EditorMode.CHIP_RECOMMENDATION);
const isBaseChipMode = computed(() => props.editorMode === EditorMode.CHIP);
const isChipMode = computed(() => isBaseChipMode.value || isChipRecommendationMode.value);
const isDetailBaseMode = computed(() => props.editorMode === EditorMode.DETAIL);
const isDetailRecommendationMode = computed(() => props.editorMode === EditorMode.DETAIL_RECOMMENDATION);
const isTaskDetailMode = computed(() => isDetailBaseMode.value || isDetailRecommendationMode.value);
const isRecommendationMode = computed(() => isChipRecommendationMode.value || isDetailRecommendationMode.value);
const isContextMenuMode = computed(() => props.editorMode === EditorMode.CONTEXT_MENU);
const isFormMode = computed(() => props.editorMode === EditorMode.FORM);
const isAutomationMode = computed(() => props.editorMode === EditorMode.AUTOMATION);
const isFormOrAutomationMode = computed(() => isFormMode.value || isAutomationMode.value);

const selectedTasks = computed(() =>
  isChipMode.value || isFormOrAutomationMode.value
    ? props.tasks
    : dataStore.getTasksByDuidsOrderedIfLoaded([...appStore.selectedTaskDuids], {
        includeTrashed: false,
        includeDraft: false,
      })
);
const selectedTaskDuids = computed(() => selectedTasks.value.map((e) => e.duid));

const optionDuids = computed(
  () =>
    props.values ?? [
      ...unionOfAllSets(props.tasks.map((e) => getPropertyValueFromTask(props.property, e)).map((e) => new Set(e))),
    ]
);
const isUnsetSelected = computed(() => optionDuids.value.includes(UNSET_PROPERTY_PSEUDO_VALUE));

const replaceOptions = (newOptionDuids: string[]) => {
  emit("update", newOptionDuids);
  if (isFormOrAutomationMode.value) {
    return;
  }

  dataStore.replaceOptions(props.property, selectedTaskDuids.value, newOptionDuids);
};

const onAdd = (optionDuid: string | null) => {
  if (optionDuid === null) {
    replaceOptions([]);
    return;
  }

  if (isFormOrAutomationMode.value) {
    emit("update", [...new Set([...optionDuids.value.filter((e) => e !== UNSET_PROPERTY_PSEUDO_VALUE), optionDuid])]);
    return;
  }

  dataStore.addOptions(props.property, selectedTaskDuids.value, [optionDuid]);
  emit("update");
};

const onRemove = (optionDuid: string | null) => {
  if (optionDuid === null) {
    replaceOptions([]);
    return;
  }

  if (isFormOrAutomationMode.value) {
    emit(
      "update",
      optionDuids.value.filter((e) => e !== optionDuid)
    );
    return;
  }

  dataStore.removeOptions(props.property, selectedTaskDuids.value, [optionDuid]);
  emit("update");
};

const optionSections = computed(() => {
  const selectedOptionDuids = isFormOrAutomationMode.value
    ? (props.values ?? [])
    : [
        ...intersectionOfAllSets(
          selectedTasks.value.map((e) => getPropertyValueFromTask(props.property, e)).map((e) => new Set(e))
        ),
      ];

  return [
    {
      title: "Options",
      items: dataStore.getOptionList(props.property).map((option) => {
        const selected = selectedOptionDuids.includes(option.duid);
        return {
          value: option.duid,
          label: option.title,
          selected,
          component: OptionDropdownItem,
          componentArgs: { option, selected },
        };
      }),
    },
  ];
});

const activeOptions = computed(() => dataStore.getOptionsByDuidsOrdered(optionDuids.value, props.property));

const onCreate = async (optionTitle: string) => {
  if (isFormOrAutomationMode.value) {
    const option = await dataStore.createOption(optionTitle, props.property);
    if (!option) {
      return;
    }

    emit("update", [...optionDuids.value, option.duid]);
    return;
  }

  dataStore.addOptions(props.property, isFormOrAutomationMode.value ? [] : selectedTaskDuids.value, [], {
    createTitle: optionTitle,
  });
  emit("update");
};

const generateRecommendation = async () => {
  const tempSelectedTasks = selectedTaskDuids.value;
  if (tempSelectedTasks.length > 100) {
    notify({ message: "Dart AI cannot fill out more than 100 tasks at once", type: NotificationType.ERROR });
    return;
  }

  appStore.startLoadingAiTaskProperties(tempSelectedTasks, props.property.duid);
  const [taskUpdates, recDuids] = await generateFieldRecommendation(
    selectedTasks.value,
    "tagDuids",
    props.defaultValue ?? []
  );
  dataStore.updateTasks(taskUpdates);
  appStore.finishLoadingAiTaskProperties(tempSelectedTasks, props.property.duid);

  nextTick(() => {
    actions.visualization.showFeedbackTooltipForTask(
      tempSelectedTasks,
      recDuids,
      props.editorMode,
      wrapper.value ?? null
    );
  });
};

const rejectRecommendation = () => {
  replaceOptions(props.defaultValue ?? []);
  emit("reject");
};

const validateOptionTitle = computed(() =>
  makeValidateOptionTitle((title) => dataStore.getOptionByTitle(title, props.property, null))
);

const hiddenOptionCount = ref(0);
const areAllOptionsHidden = computed(() => hiddenOptionCount.value === activeOptions.value.length);
const options = computed(() => {
  const allOptions = activeOptions.value.slice(0, activeOptions.value.length - hiddenOptionCount.value);
  if (areAllOptionsHidden.value && activeOptions.value.length > 0) {
    return activeOptions.value.slice(0, 1);
  }
  return allOptions;
});
const hiddenOptionDisplayCount = computed(() =>
  areAllOptionsHidden.value ? hiddenOptionCount.value - 1 : hiddenOptionCount.value
);
const hiddenOptionsText = computed(() => [
  {
    items: activeOptions.value.slice(-hiddenOptionDisplayCount.value).map((option) => option.title),
  },
]);

const recalculateHiddenOptions = async () => {
  if (!wrapper.value) {
    return;
  }

  hiddenOptionCount.value = 0;
  await nextTick();

  // Binary search to find the number of hidden options
  let lowerBound = 0;
  let upperBound = activeOptions.value.length;
  while (lowerBound < upperBound) {
    // Calculate the middle index between the current bounds
    const midIndex = Math.floor((lowerBound + upperBound) / 2);
    hiddenOptionCount.value = midIndex;
    // oxlint-disable-next-line no-await-in-loop
    await nextTick();
    if (!wrapper.value) {
      return;
    }
    if (wrapper.value.scrollWidth > wrapper.value.clientWidth) {
      lowerBound = midIndex + 1;
    } else {
      upperBound = midIndex;
    }
  }
  // Set the number of hidden options to the lower bound
  hiddenOptionCount.value = lowerBound;
};

watch([activeOptions, wrapper], () => {
  recalculateHiddenOptions();
});
useResizeObserver(wrapper, recalculateHiddenOptions);

const handleAfterClose = () => {
  props.onAfterClose?.(optionDuids.value);
};

watch(
  () => appStore.deleteModalConfig,
  (open) => {
    if (!open) {
      return;
    }
    dropdownMenu.value?.close();
  }
);

onUnmounted(() => {
  appStore.showFeedbackTooltip(null, []);
});

defineExpose({
  open: () => dropdownMenu.value?.open(),
  isOpen: computed(() => dropdownMenu.value?.isOpen),
});
</script>

<template>
  <MultiselectDropdownMenu
    ref="dropdownMenu"
    :container="isChipMode ? '#dart-task-creation-modal-wrapper' : isFormMode ? '#dart-form-wrapper' : undefined"
    :sections="optionSections"
    :header-title="`Set ${getPropertyDisplayTitle(property).toLowerCase()}`"
    placeholder="Add an option..."
    :block="isFormMode"
    :height-block="isFormOrAutomationMode"
    :skidding="isFormOrAutomationMode ? -1 : undefined"
    :distance="isFormOrAutomationMode ? 2 : undefined"
    :cover="!isContextMenuMode && !isFormOrAutomationMode"
    :placement="isContextMenuMode ? Placement.RIGHT_TOP : Placement.BOTTOM_LEFT"
    :enable-new-entry="!pageStore.isPublicForm"
    :validate="validateOptionTitle"
    :style="{ '--background': colors.borderVlt, '--highlight': colors.borderMd }"
    :on-after-close="handleAfterClose"
    :dont-propagate-close="!appStore.deleteModalConfig"
    @remove="onRemove"
    @add="onAdd"
    @create="onCreate">
    <template v-if="isDefault && !isFormOrAutomationMode" #footer="{ close }">
      <FillOutWithAiItem :close="close" @recommend="generateRecommendation" />
    </template>
    <DropdownMenuItemContent
      v-if="isContextMenuMode"
      :icon="TagsFieldIcon"
      :title="`Change ${getPropertyDisplayTitle(property).toLowerCase()}`"
      is-submenu />
    <Tooltip
      v-else
      :disabled="isChipRecommendationMode || isFormOrAutomationMode"
      :command-ids="isDefault ? [CommandId.EDIT_TAGS] : undefined"
      :text="isDefault ? undefined : `Change ${getPropertyDisplayTitle(property).toLowerCase()}`"
      :block="isListMode || isFormOrAutomationMode"
      :height-block="isListMode || isFormOrAutomationMode"
      class="w-full">
      <div
        v-if="isChipMode"
        ref="wrapper"
        class="flex items-center gap-1 rounded-sm border px-1 py-0.5 text-md"
        :class="
          isChipRecommendationMode
            ? 'border-recommendation-base/50 bg-recommendation-base/30 hover:bg-recommendation-base/40'
            : 'border-oncolor hover:bg-md'
        ">
        <div v-if="loading" class="flex size-full items-center justify-center px-4 py-0.5">
          <AiSpinner class="icon-sm" />
        </div>
        <template v-else>
          <TagsFieldIcon
            :class="{
              'text-recommendation-base': isChipRecommendationMode,
              'text-primary-base': isBaseChipMode && optionDuids.length !== 0,
            }"
            class="icon-sm" />
          <span class="max-w-xs truncate select-none">
            {{
              optionDuids.length === 0
                ? getPropertyDisplayTitle(property)
                : `${optionDuids.length} ${getPropertyDisplayTitle(property).toLowerCase()}`
            }}
          </span>
        </template>
        <Tooltip v-if="isRecommendationMode" text="Clear AI recommendation">
          <span
            class="rounded-sm hover:bg-recommendation-base/30 dark:hover:bg-recommendation-base/40"
            data-global-popper-ignore
            @click.stop="rejectRecommendation"
            @keydown.enter.stop="rejectRecommendation">
            <XIcon class="icon-xs" />
          </span>
        </Tooltip>
      </div>

      <div
        v-else
        ref="wrapper"
        class="group/options flex size-full cursor-pointer items-center gap-1"
        :class="{
          'truncate px-2 text-sm hover:bg-lt': isFormOrAutomationMode || isTaskDetailMode,
          'overflow-x-hidden': isListMode,
          'flex-wrap rounded py-0.5': !isListMode,
          'hover:bg-md': !isListMode && !isTaskDetailMode && !isRecommendationMode && !isFormOrAutomationMode,
          'px-1': activeOptions.length !== 0,
          'justify-start py-1 text-left': isTaskDetailMode,
          'bg-recommendation-base/10 hover:bg-recommendation-base/20 dark:hover:bg-recommendation-base/20':
            isDetailRecommendationMode,
        }">
        <div v-if="loading" class="flex size-full items-center justify-center px-4 py-0.5">
          <AiSpinner class="icon-sm" />
        </div>
        <template v-else>
          <OptionComponent
            v-for="option in options"
            :key="option.duid"
            show-ancestors
            :show-hidden-truncated-option="areAllOptionsHidden"
            :option="option"
            :truncate="!isTaskDetailMode && !isChipMode" />
          <Tooltip v-if="hiddenOptionDisplayCount > 0" :sections="hiddenOptionsText" :placement="Placement.TOP">
            <Chip id="chip" :label="`+${hiddenOptionDisplayCount}`" />
          </Tooltip>
          <template v-if="!isListMode && !activeOptions.length">
            <div v-if="isTaskDetailMode || isFormOrAutomationMode" class="truncate text-vlt select-none">
              {{
                isUnsetSelected
                  ? "(Unset)"
                  : isTaskDetailMode
                    ? "None"
                    : `Set ${getPropertyDisplayTitle(property).toLowerCase()}`
              }}
            </div>
            <PlusIcon v-else class="icon-sm" />
          </template>
          <div
            v-if="isListMode && !activeOptions.length"
            class="flex size-full items-center justify-center text-transparent group-hover/options:text-vlt">
            <TagsFieldIcon class="icon-xs" />
          </div>
        </template>
        <Tooltip v-if="isRecommendationMode" text="Clear AI recommendation">
          <span
            class="rounded-sm text-md hover:bg-recommendation-base/30 dark:hover:bg-recommendation-base/40"
            data-global-popper-ignore
            @click.stop="rejectRecommendation"
            @keydown.enter.stop="rejectRecommendation">
            <XIcon class="icon-xs" />
          </span>
        </Tooltip>
      </div>
    </Tooltip>
  </MultiselectDropdownMenu>
</template>
